import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
const fontRoles = [{
  role: '01',
  name: 'Anton',
  use: 'Display / Wordmark',
  appliedTo: 'Logo only. Not for headlines or body.',
  sampleClass: 'font-display-condensed italic uppercase',
  sample: 'metro',
  weights: 'Regular',
  source: 'Google Fonts'
}, {
  role: '02',
  name: 'Space Grotesk',
  use: 'Display / Headlines',
  appliedTo: 'Large titles, editorial headlines, section heads, accent text.',
  sampleClass: 'font-space font-medium',
  sample: 'A network of ideas.',
  weights: 'Light · Regular · Medium · Bold',
  source: 'Google Fonts'
}, {
  role: '03',
  name: 'Helvetica Neue',
  use: 'Body / UI',
  appliedTo: 'Long-form body, captions, buttons, navigation, micro-copy.',
  sampleClass: 'font-helvetica',
  sample: 'Capabilities that intersect. Projects that route through them.',
  weights: 'Regular · Medium',
  source: 'System font (fallbacks: Helvetica, Arial)'
}];
const typeScale = [{
  name: 'D1 / Display',
  size: '120px',
  weight: 'Medium',
  class: 'font-space font-medium'
}, {
  name: 'H1 / Heading',
  size: '64px',
  weight: 'Medium',
  class: 'font-space font-medium'
}, {
  name: 'H2 / Section',
  size: '40px',
  weight: 'Medium',
  class: 'font-space font-medium'
}, {
  name: 'H3 / Subhead',
  size: '24px',
  weight: 'Regular',
  class: 'font-space'
}, {
  name: 'Body L',
  size: '18px',
  weight: 'Regular',
  class: 'font-helvetica'
}, {
  name: 'Body M',
  size: '16px',
  weight: 'Regular',
  class: 'font-helvetica'
}, {
  name: 'Caption',
  size: '13px',
  weight: 'Regular',
  class: 'font-helvetica'
}, {
  name: 'Mono',
  size: '12px',
  weight: 'Regular',
  class: 'font-mono'
}];
export function TypographySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="typography" ref={ref} className="min-h-screen px-8 py-24" data-id="element-1596">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-1597">
        <div className="mb-16" data-id="element-1598">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-1599">
            02 / TYPOGRAPHY
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-1600">
            Type System
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 max-w-2xl font-helvetica" data-id="element-1601">
            A three-role stack. Anton holds the wordmark. Space Grotesk carries
            headlines and editorial. Helvetica Neue runs the long-form, the UI,
            the everyday.
          </p>
        </div>

        <div className="space-y-6 mb-24" data-id="element-1602">
          {fontRoles.map((role, index) => <motion.div key={role.name} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1603">
              <div className="lg:col-span-3 space-y-3" data-id="element-1604">
                <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1605">
                  {role.role} / {role.use.toUpperCase()}
                </div>
                <div className="font-space text-2xl font-medium" data-id="element-1606">
                  {role.name}
                </div>
                <div className="font-helvetica text-sm text-[var(--text-muted)]" data-id="element-1607">
                  {role.appliedTo}
                </div>
                <div className="pt-3 space-y-1" data-id="element-1608">
                  <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1609">
                    WEIGHTS — {role.weights}
                  </div>
                  <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1610">
                    SOURCE — {role.source}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-9 flex items-center min-h-[160px]" data-id="element-1611">
                <div className={`${role.sampleClass} text-5xl md:text-7xl leading-[1] tracking-tight`} data-id="element-1612">
                  {role.sample}
                </div>
              </div>
            </motion.div>)}
        </div>

        <div data-id="element-1613">
          <div className="font-mono text-xs text-[var(--text-muted)] mb-6" data-id="element-1614">
            TYPE SCALE
          </div>
          <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] divide-y divide-[var(--divider)]" data-id="element-1615">
            {typeScale.map(scale => <div key={scale.name} className="p-6 flex items-center justify-between gap-6" data-id="element-1616">
                <div className="flex-shrink-0 w-48" data-id="element-1617">
                  <div className="font-mono text-xs text-[var(--text-muted)] mb-1" data-id="element-1618">
                    {scale.name}
                  </div>
                  <div className="font-helvetica text-sm text-[var(--text-muted)]" data-id="element-1619">
                    {scale.size} / {scale.weight}
                  </div>
                </div>
                <div className={`flex-1 text-right truncate ${scale.class}`} style={{
              fontSize: scale.size,
              lineHeight: 1.1
            }} data-id="element-1620">
                  Aa
                </div>
              </div>)}
          </div>
        </div>
      </motion.div>
    </section>;
}